'use client';

// The page header's clamped title and description (page header contract H5 and H6, #15269 S6).
// Shared by PageHeader and SurfaceHeader so the two cannot drift. Internal: not exported from the
// package index.
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useIsClamped } from '../../lib/use-is-clamped';
import { Tooltip } from '../Tooltip/Tooltip';

// H5: one line from `sm` up, up to two below it, never three. The clamp is on an element INSIDE the
// h1, because an inline-flex icon wrapper cannot be clamped across; the icon sits beside it.
//
// `pr-1 -mr-1`: `overflow: hidden` on a box that ends at the last glyph's advance cuts italic and
// swash overhang (the independent judge measured 1 to 3 px of ink). The padding gives the clip box
// room and the equal negative margin keeps the layout width, so no title moves.
//
// Complete literals, never interpolated next to `${}` (check-tailwind-class-interpolation).
const TITLE_CLAMP = 'min-w-0 break-words line-clamp-2 sm:line-clamp-1 pr-1 -mr-1';

interface ClampedHeadingProps {
  title: ReactNode;
  /** The h1's own classes (size, colour); the clamp is not on the h1. */
  className: string;
  /** Wayfinding icon beside the title, outside the clamped element. */
  icon?: ReactNode;
}

/**
 * The h1, with the title clamped and its full text in a tooltip ONLY when it is clamped (measured).
 * `title` is a ReactNode, so a tooltip needs a string source: a title that is not a string is
 * clamped but has no tooltip (its full text is still in the DOM and the accessible name).
 *
 * The Tooltip wraps the h1, not the inner span: its wrapper is `inline-block`, and an inline-block
 * around a block box inside a block parent adds a strut line box. Every parent of this heading is
 * a flex container, so the wrapper is a flex item and adds none. The wrapper carries the h1's old
 * `min-w-0` so it can shrink and the clamped text breaks, instead of overflowing the row.
 *
 * The icon wrapper stays `inline-flex`, exactly as before the clamp: its line box makes a one-line
 * h1 34px tall, and a block `flex` wrapper made it 32px, which would have moved every header that
 * shows a nav icon up by 2px (measured). `max-w-full` is what lets the clamp work inside it.
 */
export function ClampedHeading({ title, className, icon }: ClampedHeadingProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const clamped = useIsClamped(ref, title);
  const text = typeof title === 'string' ? title : undefined;
  const clampedTitle = (
    <span ref={ref} className={TITLE_CLAMP}>
      {title}
    </span>
  );
  return (
    <Tooltip
      content={text ?? ''}
      disabled={text === undefined || !clamped}
      className="min-w-0 max-w-full"
    >
      <h1 className={className}>
        {icon ? (
          <span className="inline-flex max-w-full items-center gap-2">
            {icon}
            {clampedTitle}
          </span>
        ) : (
          clampedTitle
        )}
      </h1>
    </Tooltip>
  );
}

// H6: a 68ch measure, two lines at every width. `min-w-0` lets the paragraph shrink beside the toggle;
// `break-words` (as the title clamp has) wraps a long unbreakable word instead of letting the clamp
// clip it once the toggle has taken its width.
// Complete literals, never interpolated next to `${}` (check-tailwind-class-interpolation).
const DESCRIPTION_MEASURE = 'min-w-0 max-w-[68ch] break-words';
// A printed header shows the whole description: paper has no "More".
const DESCRIPTION_CLAMP = 'line-clamp-2 print:line-clamp-none';

interface ClampedDescriptionProps {
  children: ReactNode;
  /** The paragraph's type role and colour (the two headers differ). */
  className: string;
  /** The toggle's type role only, so its line box matches the paragraph's and its baseline sits on the last line. */
  toggleClassName: string;
}

/**
 * H6: the description, clamped to two lines under a 68ch measure. When the text really is cut
 * (measured, never a character count: `description` is a ReactNode) a "More" toggle shows the rest
 * in place; the full text is always in the DOM, clamped or not.
 *
 * The toggle sits in the same flex row as the paragraph, bottom-aligned, so it ends the last line
 * instead of adding a row. Where nothing can be measured (no layout: jsdom, or `display: none`) the
 * text counts as fitting and there is no toggle, so a hidden header or a test render never grows a
 * button. Showing the toggle narrows the paragraph by the toggle's width, which can only make the
 * text more clamped, so the measure cannot flip back and forth. Once open the clamp is gone and the
 * text reads as fitting, so the toggle stays while `expanded`.
 *
 * It is ONE button for its whole life: closing must not unmount it, or the keyboard focus on it
 * is lost. The reading taken while the text was open says "fits", so the render that closes it
 * would drop the toggle; `closing` holds it through that one render, and `expanded` is part of the
 * measure's key so the closed text is measured in the same layout pass that commits the close.
 *
 * A different string description (a detail page that keeps this header mounted while the operator
 * moves to the next record) starts closed: otherwise a short new description would render open
 * with a stray "Less". A description that is not a string cannot be compared, so it keeps its state.
 */
export function ClampedDescription({
  children,
  className,
  toggleClassName,
}: ClampedDescriptionProps) {
  const id = useId();
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const text = typeof children === 'string' ? children : null;
  const [seenText, setSeenText] = useState(text);
  if (text !== seenText) {
    setSeenText(text);
    setExpanded(false);
  }
  // True from the click that closes the description until the next layout pass has measured the
  // closed text (see above).
  const [closing, setClosing] = useState(false);
  const clamped = useIsClamped(ref, [children, expanded], { whenUnmeasurable: false });
  useLayoutEffect(() => {
    if (closing) setClosing(false);
  }, [closing]);
  function toggle() {
    if (expanded) setClosing(true);
    setExpanded(!expanded);
  }
  return (
    <div className="mt-1 flex items-end gap-2">
      <p
        ref={ref}
        id={id}
        className={[className, DESCRIPTION_MEASURE, !expanded && DESCRIPTION_CLAMP]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </p>
      {clamped || expanded || closing ? (
        // raw-button-ok: the description's own "More"/"Less" disclosure. <Button variant="link"> draws brand-green text with a hover-only underline, which fails 4.5:1 on the page background (4.41 light, 4.05 dark, measured), and this control needs a line box that matches the paragraph so it adds no row (2026-10-18)
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={toggle}
          // `text-primary` plus an underline: the brand green as text fails 4.5:1 on the page background
          // (4.41 light, 4.05 dark, measured by the independent judge), and the underline says it is a
          // control without relying on colour.
          className={[
            toggleClassName,
            'min-h-6 shrink-0 rounded-control px-1 text-primary underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus print:hidden',
          ].join(' ')}
        >
          {expanded ? 'Less' : 'More'}
        </button>
      ) : null}
    </div>
  );
}
